// My Mail / My Calendar — two personal, read-only My Desk panels (owner,
// 2026-09-10 / "build it" 2026-09-18). Each reads the SIGNED-IN person's own
// Gmail inbox or Google Calendar through the same domain-wide-delegation
// path `inbox_badges.js` already uses server-side — see `src/my_google.js`
// for the wire contract this file is a thin, typed client for.
//
// Mirrors `lib/licenses.ts`'s own shape deliberately: a `fetchX()` for
// `useResource`, a `KEY` constant, and a `reloadX()` "re-fetch what's
// already cached" helper for a panel's own Refresh button.
//
// Calendar has NO Workspace Admin Console grant yet (the owner adds it
// later) — the server answers `{ connected: false }` with HTTP 200 for
// that, same as Mail with no grant or a non-domain email; NEITHER of those
// is an error. The one thing that IS an HTTP error is `ctx.viewingAs`
// (owner/director viewing the app as someone else): the server refuses
// with 403 and this EXACT body, so `authedGet` throws an `ApiError` whose
// `.message` is this literal string — the panels check for it by name
// (P2) rather than trying to read a status code back out of the shared
// resource cache's `Load<T>`, which only keeps a string.
import { authedGet } from './apiClient';
import { refetchResource } from './store';
import { parseDateOnly, formatDate } from './date';
import { clinicToday, clinicNow } from './clinicDay';
import { addDaysIso } from './civilDate';

export const VIEWING_AS_BLOCKED_MESSAGE = 'not available while viewing as someone else';

export const MY_MAIL_KEY = 'my-mail';
export const MY_CALENDAR_KEY = 'my-calendar';

// --- Mail ------------------------------------------------------------------

export interface MyMailThread {
  id: string;
  from: string;
  subject: string;
  /** Gmail's own one-line preview (entities decoded, capped server-side). */
  snippet?: string;
  receivedAt: string;
  unread: boolean;
  messageCount: number;
  url: string;
}

export interface MyMailData {
  connected: boolean;
  unreadTotal: number | null;
  /** Threads Gmail listed but would not return details for (a busy
   * mailbox) — said out loud by the panel, never a silent shorter list. */
  missing?: number;
  /** Unread first, then newest first (server order). */
  threads: MyMailThread[];
}

/** "now", "12m ago", "3h ago", "Yesterday", "4d ago", then MM/DD/YYYY —
 * on the clinic's (Eastern) clock. `now` is injectable for tests. */
export function relativeTime(iso: string | null | undefined, now: Date = new Date()): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const minutes = Math.floor((now.getTime() - d.getTime()) / 60000);
  if (minutes < 1) return 'now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  const sameDay = localDayKey(d) === localDayKey(now);
  if (sameDay || hours < 12) return `${hours}h ago`;
  const days = dayDiff(localDayKey(d), localDayKey(now));
  if (days <= 1) return 'Yesterday';
  if (days < 7) return `${days}d ago`;
  return formatDate(clinicNow(d));
}

export function fetchMyMail(): Promise<MyMailData> {
  return authedGet('/api/v1/my/mail');
}

/** Re-fetch mail already cached under `MY_MAIL_KEY` — a panel's own Refresh
 * button, same idiom as `licenses.ts`'s `reloadLicenses`. */
export async function reloadMyMail(): Promise<void> {
  await refetchResource(MY_MAIL_KEY, fetchMyMail);
}

// --- Calendar ----------------------------------------------------------------

export interface MyCalendarEvent {
  id: string;
  title: string;
  /** ISO datetime for a timed event, or a bare "YYYY-MM-DD" when `allDay`. */
  start: string;
  end: string;
  allDay: boolean;
  location: string | null;
  url: string | null;
}

export interface MyCalendarData {
  connected: boolean;
  days: number;
  events: MyCalendarEvent[];
}

export function fetchMyCalendar(): Promise<MyCalendarData> {
  return authedGet('/api/v1/my/calendar');
}

export async function reloadMyCalendar(): Promise<void> {
  await refetchResource(MY_CALENDAR_KEY, fetchMyCalendar);
}

/** One calendar day's events, in the order they arrived (the server already
 * sorts ascending by start). */
export interface MyCalendarDayGroup {
  /** "YYYY-MM-DD", the clinic's (Eastern) calendar day. */
  key: string;
  events: MyCalendarEvent[];
}

/** Calendar-day key for a timed event's `start` — the CLINIC's (Eastern) day,
 * never the viewer's computer (owner ruling, 2026-10-16). An all-day event
 * already names its own day (`start` is a bare "YYYY-MM-DD"), so it needs no
 * conversion at all. */
function localDayKey(iso: string | Date): string {
  const d = iso instanceof Date ? iso : new Date(iso);
  return clinicToday(d);
}

/** Group events (already ascending by start) under their own local day,
 * preserving arrival order both across and within groups. */
export function groupCalendarEventsByDay(events: MyCalendarEvent[]): MyCalendarDayGroup[] {
  const groups: MyCalendarDayGroup[] = [];
  const index = new Map<string, MyCalendarDayGroup>();
  for (const event of events) {
    const key = event.allDay ? event.start : localDayKey(event.start);
    let group = index.get(key);
    if (!group) {
      group = { key, events: [] };
      index.set(key, group);
      groups.push(group);
    }
    group.events.push(event);
  }
  return groups;
}

/** A day group's own heading — MM/DD/YYYY, parsed as a local calendar day
 * (never `new Date(dayKey)` directly; see `parseDateOnly`'s own comment). */
export function calendarDayHeading(dayKey: string): string {
  return formatDate(parseDateOnly(dayKey));
}

// --- Calendar sections (2026-10-11 redesign) ---------------------------------

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function addDays(dayKey: string, n: number): string {
  // Pure calendar arithmetic on the day key — no clock, no time zone.
  return addDaysIso(dayKey, n);
}

/** Whole local days from `a` to `b` ("YYYY-MM-DD" each). */
function dayDiff(a: string, b: string): number {
  return Math.round((parseDateOnly(b).getTime() - parseDateOnly(a).getTime()) / 86400000);
}

export interface MyCalendarSectionEvent extends MyCalendarEvent {
  /** A timed event today that has already ended — shown quieter. */
  past: boolean;
}

export interface MyCalendarSections {
  todayKey: string;
  today: MyCalendarSectionEvent[];
  /** The next 7 days after today, only the days that have something. */
  upcoming: Array<{ key: string; events: MyCalendarSectionEvent[] }>;
}

/** Today first, then the next 7 days grouped by local day. An event that
 * began before today but is still running (a multi-day all-day event, an
 * overnight shift) shows under Today. All-day events lead each day. */
export function calendarSections(events: MyCalendarEvent[], now: Date = new Date()): MyCalendarSections {
  const todayKey = localDayKey(now);
  const lastKey = addDays(todayKey, 7);
  const byDay = new Map<string, MyCalendarSectionEvent[]>();
  for (const event of events) {
    let startKey: string;
    let lastDayKey: string;
    let past = false;
    if (event.allDay) {
      startKey = event.start;
      // Google's all-day `end` is the day AFTER the last day.
      lastDayKey = event.end ? addDays(event.end, -1) : event.start;
    } else {
      const start = new Date(event.start);
      const end = new Date(event.end || event.start);
      if (Number.isNaN(start.getTime())) continue;
      startKey = localDayKey(start);
      lastDayKey = localDayKey(new Date(Math.max(start.getTime(), end.getTime() - 1)));
      past = end.getTime() <= now.getTime();
    }
    if (lastDayKey < todayKey) continue;
    const key = startKey < todayKey ? todayKey : startKey;
    if (key > lastKey) continue;
    if (key !== todayKey) past = false;
    const list = byDay.get(key) ?? [];
    list.push({ ...event, past });
    byDay.set(key, list);
  }
  const order = (e: MyCalendarSectionEvent): number => (e.allDay ? -Infinity : new Date(e.start).getTime());
  for (const list of byDay.values()) list.sort((a, b) => order(a) - order(b));
  const upcoming = [...byDay.keys()]
    .filter((k) => k !== todayKey)
    .sort()
    .map((key) => ({ key, events: byDay.get(key) ?? [] }));
  return { todayKey, today: byDay.get(todayKey) ?? [], upcoming };
}

/** "Tomorrow", or "Tue 10/13/2026" — weekday plus the house MM/DD/YYYY. */
export function upcomingDayHeading(dayKey: string, todayKey: string): string {
  if (dayKey === addDays(todayKey, 1)) return 'Tomorrow';
  const d = parseDateOnly(dayKey);
  return `${WEEKDAYS[d.getDay()]} ${formatDate(d)}`;
}
